<template>
  <!-- badge: tres barras chicas para la tabla (CPU, RAM, disco) -->
  <span v-if="mode == 'badge'">
    <span v-if="!resources" class="text-muted">—</span>
    <span v-else class="monitor-resources-cell"
          v-b-tooltip.hover.html="{ title: tooltip, placement: 'left', boundary: 'window', customClass: 'monitor-status-tip' }">
      <span v-for="row in rows" :key="row.key" class="monitor-resources-cell__row">
        <span class="monitor-resources-cell__label">{{row.label}}</span>
        <span class="monitor-resources__bar"><span :class="'monitor-resources__fill--' + row.tone" :style="{ width: row.uso + '%' }"></span></span>
        <span class="monitor-resources-cell__value" :class="'text-' + row.tone">{{formatPercent(row.uso)}}</span>
        <span class="monitor-resources-cell__total">{{row.total || '—'}}</span>
      </span>
    </span>
  </span>

  <!-- detail: panel lateral -->
  <div v-else>
    <div v-if="!resources" class="text-muted">Sin datos reportados</div>
    <div v-else class="monitor-detail">
      <div class="monitor-resources__tiles">
        <div v-for="row in rows" :key="row.key" class="monitor-resources__tile">
          <div class="monitor-resources__tile-head">
            <span class="monitor-detail__tile-label"><AppIcon :name="row.icon" /> {{row.label}}</span>
            <b :class="'text-' + row.tone">{{formatPercent(row.uso)}}</b>
          </div>
          <span class="monitor-resources__bar monitor-resources__bar--lg"><span :class="'monitor-resources__fill--' + row.tone" :style="{ width: row.uso + '%' }"></span></span>
          <span class="monitor-detail__tile-meta">{{row.detail}}</span>
        </div>
      </div>
      <div v-if="resources.encendido_desde" class="monitor-detail__row">
        <span class="monitor-detail__label"><AppIcon name="power_settings_new" /> Encendido</span>
        <span class="monitor-detail__value" v-b-tooltip.hover :title="'Desde el ' + formatDate(resources.encendido_desde)">{{capitalize(timeAgo(resources.encendido_desde))}}</span>
      </div>
      <div v-if="resources.sistema_operativo" class="monitor-detail__row">
        <span class="monitor-detail__label"><AppIcon name="terminal" /> Sistema operativo</span>
        <span class="monitor-detail__value">{{resources.sistema_operativo}}</span>
      </div>
      <div v-if="resources.fecha_actualizacion" class="monitor-detail__footer">
        <AppIcon name="update" /> Relevado {{timeAgo(resources.fecha_actualizacion)}} · {{formatDate(resources.fecha_actualizacion)}}
      </div>
    </div>
  </div>
</template>

<script>
  import { parseJson, resourceTone, timeAgo, formatDate, RESOURCE_WARN_PERCENT, RESOURCE_CRITICAL_PERCENT } from './monitorConfig'
  import './monitorDetail.css'

  // formato de monitor_recursos (definido con Monitor):
  // {cpu: {nucleos?, uso}, ram: {total, usado, uso}, disco: {total, usado, uso},
  //  encendido_desde?, sistema_operativo?, fecha_actualizacion}
  export default {
    props: {
      value: { default: null },
      mode: { type: String, default: 'badge' },
    },
    computed: {
      resources() {
        var data = parseJson(this.value)
        return data && typeof data == 'object' && data.cpu && data.ram && data.disco ? data : null
      },
      rows() {
        if(!this.resources) return []
        var r = this.resources
        return [
          { key: 'cpu', label: 'CPU', icon: 'memory', uso: r.cpu.uso, tone: this.tone(r.cpu.uso), total: r.cpu.nucleos ? r.cpu.nucleos + (r.cpu.nucleos == 1 ? ' núcleo' : ' núcleos') : '', detail: r.cpu.nucleos ? r.cpu.nucleos + ' núcleos' : '' },
          { key: 'ram', label: 'RAM', icon: 'developer_board', uso: r.ram.uso, tone: this.tone(r.ram.uso), total: r.ram.total, detail: r.ram.usado + ' de ' + r.ram.total },
          { key: 'disco', label: 'Disco', icon: 'hard_drive', uso: r.disco.uso, tone: this.tone(r.disco.uso), total: r.disco.total, detail: r.disco.usado + ' de ' + r.disco.total },
        ]
      },
      // toda la información, en tabla (mismo estilo que el estado de instancias)
      tooltip() {
        if(!this.resources) return ''
        var r = this.resources
        var esc = text => String(text === undefined || text === null ? '' : text).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c])
        var row = (label, value) => '<tr><th>' + label + '</th><td>' + value + '</td></tr>'
        var html = row('CPU', this.formatPercent(r.cpu.uso) + (r.cpu.nucleos ? ' · ' + r.cpu.nucleos + ' núcleos' : ''))
        html += row('RAM', this.formatPercent(r.ram.uso) + ' · ' + esc(r.ram.usado) + ' de ' + esc(r.ram.total))
        html += row('Disco', this.formatPercent(r.disco.uso) + ' · ' + esc(r.disco.usado) + ' de ' + esc(r.disco.total))
        if(r.encendido_desde) html += row('Encendido', formatDate(r.encendido_desde) + ' (' + timeAgo(r.encendido_desde) + ')')
        if(r.sistema_operativo) html += row('Sistema', esc(r.sistema_operativo))
        if(r.fecha_actualizacion) html += row('Relevado', formatDate(r.fecha_actualizacion) + ' (' + timeAgo(r.fecha_actualizacion) + ')')
        html += '<tr class="monitor-status-tip__legend"><td colspan="2">Uso: verde hasta ' + RESOURCE_WARN_PERCENT + ' %, amarillo hasta ' + RESOURCE_CRITICAL_PERCENT + ' %, rojo más.</td></tr>'
        return '<table class="monitor-status-tip__table">' + html + '</table>'
      },
    },
    methods: {
      timeAgo,
      formatDate,
      tone(value) {
        return resourceTone(value)
      },
      formatPercent(value) {
        return Math.round(value) + ' %'
      },
      capitalize(text) {
        return text ? text.charAt(0).toUpperCase() + text.slice(1) : text
      },
    },
  }
</script>

<style scoped>
  .monitor-resources-cell {
    display: inline-flex;
    flex-direction: column;
    gap: 2px;
    max-width: 100%;
    cursor: default;
  }

  .monitor-resources-cell__row {
    display: grid;
    grid-template-columns: 34px minmax(24px, 64px) 38px auto;
    align-items: center;
    gap: 6px;
    font-size: 11px;
  }

  .monitor-resources-cell__label {
    color: var(--app-color-text-secondary);
    font-weight: 600;
  }

  .monitor-resources-cell__value {
    font-weight: 700;
    text-align: right;
  }

  /* capacidad total del recurso (núcleos, RAM, disco) */
  .monitor-resources-cell__total {
    color: var(--app-color-text-muted);
    white-space: nowrap;
  }

  .monitor-resources__bar {
    display: block;
    height: 6px;
    border-radius: 999px;
    background: var(--app-color-surface-subtle);
    overflow: hidden;
  }

  .monitor-resources__bar--lg {
    height: 8px;
    margin: 4px 0;
  }

  .monitor-resources__bar > span {
    display: block;
    height: 100%;
    border-radius: 999px;
  }

  .monitor-resources__fill--success { background: var(--app-color-success); }
  .monitor-resources__fill--warning { background: var(--app-color-warning); }
  .monitor-resources__fill--danger { background: var(--app-color-danger); }

  .monitor-resources__tiles {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    padding: 10px 12px;
    border-bottom: 1px solid var(--app-color-border);
  }

  .monitor-resources__tile {
    padding: 8px 10px;
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    background: var(--app-color-surface-subtle);
    min-width: 0;
  }

  .monitor-resources__tile-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
  }

  .monitor-resources__tile-head b {
    font-size: 14px;
  }
</style>
